import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, Plural } from '@lingui/react/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { differenceInDays } from 'date-fns';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';
import { Button, Tooltip } from '@ygtechnologies/rubus-ui';

import { DateDisplay } from '@/components/localization';
import { BuyerProjectDeadline } from '@/types';
import { useIsImpersonating } from '@/utils/hooks';
import { useOptionConfigurator } from '@/components/OptionConfigurator/OptionConfiguratorContext';
import theme from './DeadlineBlock.module.scss';

const NumberOfDaysBeforeWarning = 14;

interface Props {
    deadline: BuyerProjectDeadline;
    unsignedCount: number;
    canSign: boolean;
    submitUrl: string;
}

export const DeadlineHeader: React.FC<Props> = ({ deadline, unsignedCount, canSign, submitUrl }) => {
    // Match the new configurator: signing is only blocked while impersonating a buyer.
    // Sellers (not impersonating) are allowed to sign. See OptionConfiguratorContext `viewOnly`.
    const viewOnly = useIsImpersonating();
    const { isShowroom } = useOptionConfigurator();
    const daysRemaining = differenceInDays(parseISODate(deadline.deadline), new Date());
    const expired = daysRemaining < 0;

    // In the showroom deadlines are irrelevant: show just the deadline name as a section label,
    // with none of the date / expiry / urgency / signing cues.
    if (isShowroom) {
        return (
            <div className={theme.header}>
                <div className={theme.headerLeft}>
                    <div>
                        <div className={theme.title}>{deadline.name}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={theme.header}>
            <div className={theme.headerLeft}>
                {!expired && daysRemaining < NumberOfDaysBeforeWarning && (
                    <Tooltip>
                        <Tooltip.Trigger>
                            <span className={theme.warningIcon}>
                                <FontAwesomeIcon icon={faExclamationCircle} />
                            </span>
                        </Tooltip.Trigger>
                        <Tooltip.Content>
                            <Trans>{daysRemaining} days remaining until deadline</Trans>
                        </Tooltip.Content>
                    </Tooltip>
                )}
                {expired && (
                    <span className={theme.warningIcon}>
                        <FontAwesomeIcon icon={faExclamationCircle} />
                    </span>
                )}
                <div>
                    <div className={theme.title}>
                        <Trans>
                            Options - {deadline.name} - <DateDisplay value={deadline.deadline} />
                        </Trans>
                        {expired && (
                            <>
                                - <Trans>this deadline has expired.</Trans>
                            </>
                        )}
                    </div>
                    {unsignedCount > 0 && (
                        <div className={theme.unsigned}>
                            <Plural
                                one="# option is waiting for your signature. Please sign and send it."
                                other="# options are waiting for your signature. Please sign and send your options."
                                value={unsignedCount}
                            />
                        </div>
                    )}
                </div>
            </div>
            {canSign && (
                <Button
                    as={Link}
                    to={submitUrl}
                    variant="primary"
                    className={theme.signButton}
                    disabled={viewOnly}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <Trans>Go to sign and send</Trans>
                </Button>
            )}
        </div>
    );
};
